import { useMemo, useState } from 'react'
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Select,
  Stack, Tab, Tabs, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import SearchIcon from '@mui/icons-material/Search'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { MaintenanceRequest } from '@cmms/api-client'
import type { Site } from '@cmms/types'

import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { UserAvatar } from '../../components/UserAvatar'
import { useAuthStore } from '../../store/authStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { useConfirm } from '../../components/ConfirmDialog'
import { TreeLocationPicker } from '../../components/TreeLocationPicker'
import { SearchableSelect } from '../../components/SearchableSelect'
import { formatSiteName } from '../../lib/siteDisplayName'

// HSE fork: two-status machine — `submitted` renders as
// "Unacknowledged" and `acknowledged` is terminal. The old
// approved / declined / closed rows are kept in the map for
// legacy rows that migrated from CMMS data but no active row
// ever transitions into them.
const STATUS_META: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  submitted:    { label: 'Unacknowledged', color: '#D97706', bg: '#FFFBEB', icon: ScheduleOutlinedIcon },
  acknowledged: { label: 'Acknowledged',   color: '#059669', bg: '#ECFDF5', icon: CheckCircleOutlinedIcon },
}

// HSE fork: per-user effective status. A report is "acknowledged" from
// MY point of view when either:
//   • the whole roster has closed it out (`r.status === 'acknowledged'`), OR
//   • I personally have a roster row with `acknowledged_at` set.
// The other four people who haven't checked in yet don't change what
// the chip reads on my screen. Falls through to `'submitted'` when I
// have no roster row and the report isn't roster-wide done.
function effectiveStatus(
  r: MaintenanceRequest,
  currentUserId: string | null,
): 'submitted' | 'acknowledged' {
  if (r.status === 'acknowledged') return 'acknowledged'
  if (!currentUserId) return 'submitted'
  const mine = r.acknowledgements.find((a) => a.user_id === currentUserId)
  return mine?.acknowledged_at ? 'acknowledged' : 'submitted'
}

export function RequestsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { requestId } = useParams<{ requestId?: string }>()
  const selectedId = requestId ?? null
  const setSelectedId = (id: string | null) => navigate(id ? `/reports/${id}` : '/reports')
  const me = useAuthStore((s) => s.user)
  const { formatDate, formatDateShort } = useOrgSettings()
  const { can, isSuperAdmin } = usePermissions()
  const confirm = useConfirm()
  // HSE fork: reports have two states — submitted (unacknowledged)
  // and acknowledged (terminal). Tabs mirror that shape.
  const [tab, setTab] = useState<'all' | 'unacknowledged' | 'acknowledged'>('all')
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const requestsQ = useQuery({
    queryKey: ['requests'],
    queryFn: () => api.requests.list(),
  })
  const allRequests: MaintenanceRequest[] = Array.isArray(requestsQ.data) ? requestsQ.data : []

  const myId = me?.id ?? null
  const filtered = useMemo(() => {
    let list = allRequests
    // HSE fork: tab filter reads the per-user effective status, not
    // the report-level flag. If User 1 has already acked the report
    // (roster still open), the row lives in "Acknowledged" for them
    // but in "Unacknowledged" for the four other roster members.
    if (tab === 'unacknowledged')     list = list.filter((r) => effectiveStatus(r, myId) === 'submitted')
    else if (tab === 'acknowledged')  list = list.filter((r) => effectiveStatus(r, myId) === 'acknowledged')
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((r) =>
        r.title.toLowerCase().includes(q)
        || r.submitted_by_name?.toLowerCase().includes(q)
        || r.requester_name?.toLowerCase().includes(q)
        || r.requester_email?.toLowerCase().includes(q)
        || r.description?.toLowerCase().includes(q))
    }
    return list
  }, [allRequests, tab, search, myId])

  const counts = {
    all: allRequests.length,
    // Tab counts mirror the per-user filter above.
    unacknowledged: allRequests.filter((r) => effectiveStatus(r, myId) === 'submitted').length,
    acknowledged:   allRequests.filter((r) => effectiveStatus(r, myId) === 'acknowledged').length,
  }

  const selected = selectedId ? allRequests.find((r) => r.id === selectedId) ?? null : null

  // HSE fork: single terminal action. Decline mutation removed.
  const acknowledgeM = useMutation({
    mutationFn: (id: string) => api.requests.acknowledge(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['requests'] }); toast.success('Report acknowledged') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  // HSE fork: super_admin-only destructive delete. Backend re-checks
  // the role so a non-super caller can't slip through.
  const deleteM = useMutation({
    mutationFn: (id: string) => api.requests.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['requests'] })
      setSelectedId(null)
      toast.success('Report deleted')
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete report'),
  })
  const handleDelete = async (r: MaintenanceRequest) => {
    const ok = await confirm({
      title: 'Delete this report?',
      message: `"${r.title}" and its acknowledgement history will be permanently removed. This can't be undone.`,
      confirmLabel: 'Delete report',
      variant: 'danger',
    })
    if (ok) deleteM.mutate(r.id)
  }

  // HSE fork: linkPickerFor removed with the work-order link flow.

  const tabSx = {
    minHeight: 38,
    '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600, px: 0, mr: 3, color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } },
    '& .MuiTabs-indicator': { height: 2 },
  }

  const toolbar = (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelectedId(null) }} sx={{ ...tabSx, flex: 1 }}>
        <Tab value="all"            label={`All (${counts.all})`} />
        <Tab value="acknowledged"   label={`Acknowledged (${counts.acknowledged})`} />
        <Tab value="unacknowledged" label={`Unacknowledged (${counts.unacknowledged})`} />
      </Tabs>
      <TextField placeholder="Search reports..." value={search} onChange={(e) => setSearch(e.target.value)} size="small"
        sx={{ width: 220, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
    </Stack>
  )

  return (
    <PageShell
      title="Reports"
      subtitle="Risk reports submitted via public site QR codes and internal reporters — acknowledge to close them out."
      toolbar={toolbar}
      scrollContent={false}
      // HSE fork: "New report" button removed. Reports enter the
      // system exclusively via public site-QR scans, so an in-app
      // creation affordance would just muddy that flow.
    >
      {requestsQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>No reports</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
            {tab === 'all'            ? 'Reports submitted via public site QR codes will show up here.'
             : tab === 'acknowledged' ? 'No acknowledged reports yet.'
             :                          'Nothing waiting on acknowledgement.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Left — List */}
          <Box sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0, borderRight: { md: '1px solid' }, borderColor: 'divider',
            overflowY: 'auto', scrollbarWidth: 'thin' }}>
            <Stack spacing={0}>
              {filtered.map((r, i) => {
                // HSE fork: chip reads per-user effective status,
                // so a report I've personally acknowledged shows
                // "Acknowledged" even if teammates are still pending.
                const st = STATUS_META[effectiveStatus(r, myId)] ?? STATUS_META.submitted!
                const isActive = selectedId === r.id
                return (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(0.3, i * 0.02), duration: 0.2 }}>
                    <Box onClick={() => setSelectedId(r.id)}
                      sx={{
                        px: 2.5, py: 2, cursor: 'pointer', position: 'relative',
                        borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: isActive ? (t) => alpha(t.palette.primary.main, 0.04) : 'transparent',
                        transition: 'background 120ms',
                        '&:hover': { bgcolor: isActive ? undefined : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
                        '&::before': isActive ? {
                          content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main',
                        } : undefined,
                      }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: st.color, mt: 0.75, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, mb: 0.35 }} noWrap>{r.title}</Typography>
                          {r.description && (
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4, mb: 0.75,
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {r.description}
                            </Typography>
                          )}
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            {(() => {
                              // Same fallback chain as the detail panel —
                              // internal user first, then HSE public QR
                              // reporter, then email intake, else '—'.
                              const isPublic = !r.submitted_by_name && !!r.submitter_name
                              const isEmail  = !isPublic && r.source === 'email' && !r.submitted_by_name
                              const listName =
                                r.submitted_by_name
                                ?? (isPublic ? r.submitter_name
                                :  isEmail  ? (r.requester_name || r.requester_email || 'Email report')
                                :  null)
                              return (
                                <>
                                  <UserAvatar name={listName ?? undefined}
                                    avatarUrl={r.submitted_by_avatar}
                                    size={18} sx={{ bgcolor: '#0D9488' }} />
                                  <Typography sx={{ fontSize: 11, color: 'text.secondary',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                                    {listName ?? '—'}
                                  </Typography>
                                  {isEmail && (
                                    <Chip label="email" size="small"
                                      sx={{ height: 15, fontSize: 8.5, fontWeight: 700,
                                        bgcolor: '#F59E0B14', color: '#B45309',
                                        '& .MuiChip-label': { px: 0.6 } }} />
                                  )}
                                </>
                              )
                            })()}
                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{timeAgo(r.created_at, formatDateShort)}</Typography>
                          </Stack>
                        </Box>
                        <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                          <Chip label={st.label} size="small"
                            sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: st.bg, color: st.color }} />
                        </Stack>
                      </Stack>
                    </Box>
                  </motion.div>
                )
              })}
            </Stack>
          </Box>

          {/* Right — Detail */}
          <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}>
            <AnimatePresence mode="wait">
              {selected ? (
                <motion.div key={selected.id} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }}>
                  <RequestDetail
                    request={selected}
                    formatDate={formatDate}
                    formatDateShort={formatDateShort}
                    canManage={can('work_orders:create')}
                    onAcknowledge={() => acknowledgeM.mutate(selected.id)}
                    acknowledgePending={acknowledgeM.isPending}
                    currentUserId={me?.id ?? null}
                    canDelete={isSuperAdmin}
                    onDelete={() => handleDelete(selected)}
                    deletePending={deleteM.isPending}
                  />
                </motion.div>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 8 }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', opacity: 0.3, mb: 1.5 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>Select a request</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>Choose a request from the list to view details</Typography>
                </Box>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      )}

      {createOpen && <CreateRequestDialog onClose={() => setCreateOpen(false)} onCreated={() => { qc.invalidateQueries({ queryKey: ['requests'] }); setCreateOpen(false) }} />}

      {/* HSE fork: link-existing-work-order and decline dialogs
          removed. `acknowledged` is the only terminal state and
          Work Orders were stripped from the app entirely. */}
    </PageShell>
  )
}

// ─── Request detail panel ──────────────────────────────────────────────────

function RequestDetail({ request: r, formatDate, formatDateShort, canManage, onAcknowledge, acknowledgePending, currentUserId, canDelete, onDelete, deletePending }: {
  request: MaintenanceRequest; formatDate: (d: string) => string; formatDateShort: (d: Date | string) => string; canManage: boolean
  onAcknowledge: () => void
  acknowledgePending: boolean; currentUserId: string | null
  canDelete: boolean
  onDelete: () => void
  deletePending: boolean
}) {
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  // HSE fork: detail header chip mirrors the list card — flips to
  // "Acknowledged" the moment the current user acks their own row.
  const st = STATUS_META[effectiveStatus(r, currentUserId)] ?? STATUS_META.submitted!
  const StIcon = st.icon
  const canEdit = r.status === 'submitted' && r.submitted_by === currentUserId

  return (
    <Box sx={{ p: { xs: 2.5, md: 4 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25, mb: 1.25 }}>
            {r.title}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip icon={<StIcon sx={{ fontSize: 14 }} />} label={st.label} size="small"
              sx={{ height: 26, fontSize: 11.5, fontWeight: 700, bgcolor: st.bg, color: st.color, '& .MuiChip-icon': { color: 'inherit' } }} />
            {r.metadata?.iot_alarm && (
              <Tooltip
                title={(() => {
                  const a = r.metadata!.iot_alarm!
                  const pn = a.point_name ?? a.arcos_point_id ?? 'point'
                  const v  = a.value_at_trigger == null ? null
                    : `${a.value_at_trigger}${a.unit ? ' ' + a.unit : ''}`
                  const lim = a.limit == null ? null
                    : `${a.limit}${a.unit ? ' ' + a.unit : ''}`
                  return v && lim ? `${pn} · ${v} (limit ${lim})` : pn
                })()}
                arrow
              >
                <Chip
                  icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="Arcos alarm"
                  size="small"
                  sx={{
                    height: 26, fontSize: 11.5, fontWeight: 700,
                    bgcolor: '#FEF3C7', color: '#92400E',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {canEdit && (
            <Tooltip title="Edit request" arrow>
              <IconButton size="small" onClick={() => setEditOpen(true)}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* HSE fork: super_admin-only delete. Backend re-verifies the
              role, so hiding this button is a UX affordance rather than
              a security boundary. */}
          {canDelete && (
            <Tooltip title="Delete report" arrow>
              <IconButton size="small" onClick={onDelete} disabled={deletePending}
                sx={{ color: '#DC2626', border: '1px solid', borderColor: '#FCA5A5', borderRadius: 1.25,
                  '&:hover': { bgcolor: '#FEF2F2', borderColor: '#DC2626' } }}>
                {deletePending
                  ? <CircularProgress size={14} sx={{ color: '#DC2626' }} />
                  : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {editOpen && (
        <EditRequestDialog request={r} onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); qc.invalidateQueries({ queryKey: ['requests'] }) }} />
      )}

      {/* Description. HSE fork: public reports concatenate the
          category, escalation guidance and (optionally) the
          reporter's own note into `description`. Split those apart so
          the free-text reporter's note — the actual "why this was
          reported" — lands in a warning-styled callout that reads
          first, above the generic escalation guidance. */}
      {r.description && (() => {
        const marker = "Reporter's note:"
        const idx = r.description.indexOf(marker)
        const before = idx >= 0 ? r.description.slice(0, idx).trimEnd() : r.description
        const note   = idx >= 0 ? r.description.slice(idx + marker.length).trim() : ''
        return (
          <>
            {note && (
              <Box sx={{ mb: 2, p: 2.25, borderRadius: 2, border: '1px solid #FCD34D',
                bgcolor: '#FFFBEB' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: '#B45309', mb: 0.75 }}>
                  Reporter's note
                </Typography>
                <Typography sx={{ fontSize: 14, color: '#78350F', lineHeight: 1.65, whiteSpace: 'pre-wrap',
                  fontWeight: 500 }}>
                  {note}
                </Typography>
              </Box>
            )}
            {before && (
              <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                  Description
                </Typography>
                <Typography sx={{ fontSize: 14, color: 'text.primary', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {before}
                </Typography>
              </Box>
            )}
          </>
        )
      })()}

      {/* HSE fork: public reporter's photo attachments. Rendered as
          a small tile row that opens the original in a new tab on
          click for full-size inspection. */}
      {r.submitter_image_urls && r.submitter_image_urls.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
            Photos
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {r.submitter_image_urls.map((url, i) => (
              <Box
                key={i}
                component="a"
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                sx={{
                  display: 'block', width: 92, height: 92, borderRadius: 1.5,
                  overflow: 'hidden', border: '1px solid', borderColor: 'divider',
                  transition: 'transform 120ms ease',
                  '&:hover': { transform: 'scale(1.04)', boxShadow: 2 },
                }}
              >
                <Box component="img" src={url} alt="" loading="lazy"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {r.metadata?.iot_alarm && (
        <SourceTelemetryCard alarm={r.metadata.iot_alarm} formatDate={formatDate} />
      )}

      {/* Info cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
        <InfoCard icon={PersonOutlineIcon} label="Submitted by" color="#0D9488">
          {(() => {
            // Three sources feed this panel:
            //   1. Internal user (submitted_by row) — CMMS default.
            //   2. Email intake (r.source === 'email') — requester_*.
            //   3. HSE public risk-report QR — submitter_name/email.
            // Priority order picks the most specific match so the
            // panel never renders "—" when we actually have a name.
            const isPublic = !r.submitted_by_name && !!r.submitter_name
            const isEmail  = !isPublic && r.source === 'email' && !r.submitted_by_name
            const displayName =
              r.submitted_by_name
              ?? (isPublic ? r.submitter_name
              :  isEmail  ? (r.requester_name || r.requester_email)
              :  null) ?? '—'
            const displayEmail =
              r.submitted_by_email
              ?? (isPublic ? r.submitter_email
              :  isEmail  ? r.requester_email
              :  null)
            const avatarName =
              r.submitted_by_name
              ?? (isPublic ? r.submitter_name
              :  isEmail  ? (r.requester_name || r.requester_email)
              :  undefined) ?? undefined
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <UserAvatar name={avatarName} avatarUrl={r.submitted_by_avatar}
                  size={28} sx={{ bgcolor: '#0D9488' }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 13, fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayName}
                    </Typography>
                    {isPublic ? (
                      <Chip label="Via public QR" size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#3B82F614', color: '#1D4ED8' }} />
                    ) : isEmail ? (
                      <Chip label="Via email" size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#F59E0B14', color: '#B45309' }} />
                    ) : r.submitted_by_role ? (
                      <Chip label={r.submitted_by_role.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#0D948814', color: '#0D9488' }} />
                    ) : null}
                  </Stack>
                  {displayEmail && (
                    <Typography sx={{ fontSize: 11, color: 'text.secondary',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {displayEmail}
                    </Typography>
                  )}
                </Box>
              </Stack>
            )
          })()}
        </InfoCard>

        <InfoCard icon={CalendarTodayOutlinedIcon} label="Submitted on" color="#3B82F6">
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{formatDate(r.created_at)}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{timeAgo(r.created_at, formatDateShort)}</Typography>
        </InfoCard>

        {(r.site_name || r.location_name) && (
          <InfoCard icon={LocationOnOutlinedIcon} label="Location" color="#7C3AED">
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{r.site_name}</Typography>
            {r.location_name && <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{r.location_name}</Typography>}
          </InfoCard>
        )}

      </Box>

      {/* HSE fork: single terminal state. Acknowledged shows a
          simple green card naming who closed it out; there is no
          reject / decline reason surface anymore. */}
      {r.reviewed_by_name && r.status === 'acknowledged' && (
        <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: '1px solid #BBF7D0', bgcolor: '#F0FDF4' }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <UserAvatar name={r.reviewed_by_name} avatarUrl={r.reviewed_by_avatar} size={32}
              sx={{ bgcolor: '#059669' }} />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
                  Acknowledged by {r.reviewed_by_name}
                </Typography>
                {r.reviewed_by_role && (
                  <Chip label={r.reviewed_by_role.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())} size="small"
                    sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: '#DCFCE7', color: '#166534' }} />
                )}
              </Stack>
              {r.reviewed_at && <Typography sx={{ fontSize: 11.5, color: '#15803D', opacity: 0.7 }}>{formatDate(r.reviewed_at)}</Typography>}
            </Box>
          </Stack>
        </Box>
      )}

      {/* HSE fork: per-user acknowledgement roster + button.
          The report is fully acked only when every team member's
          row has `acknowledged_at`; the button appears only for
          users whose own row is still pending. */}
      {r.ack_total > 0 && (
        <AckRoster ack_total={r.ack_total} ack_count={r.ack_count}
          acknowledgements={r.acknowledgements}
          formatDate={formatDate} currentUserId={currentUserId} />
      )}
      {(() => {
        const mine = currentUserId
          ? r.acknowledgements.find((a) => a.user_id === currentUserId)
          : null
        // Roster-based reports: only show the button when there's a
        // roster row for me and I haven't acked yet. Roster-less
        // reports (no assigned team): keep the CMMS behaviour of
        // "any authorised user can close it out".
        const showButton = r.status === 'submitted' && (
          r.ack_total === 0
            ? canManage
            : Boolean(mine && !mine.acknowledged_at)
        )
        if (!showButton) return null
        return (
          <Button variant="contained" color="success" disabled={acknowledgePending} fullWidth
            startIcon={<CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={onAcknowledge}
            sx={{ fontSize: 13, fontWeight: 700, borderRadius: 2, py: 1.25, mt: r.ack_total > 0 ? 2 : 0 }}>
            {acknowledgePending ? 'Acknowledging…' : 'Acknowledge'}
          </Button>
        )
      })()}
    </Box>
  )
}

// HSE fork: renders the per-user acknowledgement roster inside the
// report detail. One row per team member with a green tick + timestamp
// once they've acknowledged, muted state until then. Header shows the
// running "X of Y acknowledged" count so the reader knows at a glance
// how close the report is to closing out.
function AckRoster({
  ack_total, ack_count, acknowledgements, formatDate, currentUserId,
}: {
  ack_total: number
  ack_count: number
  acknowledgements: MaintenanceRequest['acknowledgements']
  formatDate: (d: string) => string
  currentUserId: string | null
}) {
  return (
    <Box sx={{ mb: 2, p: 2.25, borderRadius: 2, border: '1px solid', borderColor: 'divider',
        bgcolor: (t) => t.palette.mode === 'light' ? '#F9FAFB' : 'rgba(255,255,255,0.02)' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
          letterSpacing: '0.06em', color: 'text.secondary' }}>
          Acknowledgements
        </Typography>
        <Chip label={`${ack_count} of ${ack_total}`} size="small"
          sx={{ height: 18, fontSize: 10, fontWeight: 700,
            bgcolor: ack_count >= ack_total ? '#DCFCE7' : '#FEF3C7',
            color:   ack_count >= ack_total ? '#166534' : '#92400E' }} />
      </Stack>
      <Stack spacing={1}>
        {acknowledgements.map((a) => {
          const done = !!a.acknowledged_at
          const isMe = a.user_id === currentUserId
          return (
            <Stack key={a.user_id} direction="row" alignItems="center" spacing={1.25}>
              <UserAvatar name={a.name} avatarUrl={a.avatar_url} size={26}
                sx={{ bgcolor: done ? '#059669' : '#94A3B8' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600,
                    color: done ? 'text.primary' : 'text.secondary',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.name}
                  </Typography>
                  {isMe && (
                    <Chip label="You" size="small" sx={{ height: 15, fontSize: 8.5,
                      fontWeight: 800, bgcolor: '#EEF2FF', color: '#3730A3' }} />
                  )}
                </Stack>
                {done ? (
                  <Typography sx={{ fontSize: 11, color: '#059669' }}>
                    Acknowledged · {formatDate(a.acknowledged_at!)}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                    Waiting…
                  </Typography>
                )}
              </Box>
              {done
                ? <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: '#059669' }} />
                : <ScheduleOutlinedIcon    sx={{ fontSize: 18, color: 'text.disabled' }} />}
            </Stack>
          )
        })}
      </Stack>
    </Box>
  )
}

// ─── Source telemetry (Arcos alarm context) ────────────────────────────────

function SourceTelemetryCard({ alarm, formatDate }: {
  alarm: NonNullable<MaintenanceRequest['metadata']>['iot_alarm']
  formatDate: (d: string) => string
}) {
  if (!alarm) return null
  const arcosBase = (import.meta.env.VITE_ARCOS_PUBLIC_URL ?? '').replace(/\/+$/, '')
  // Only render the deep-link button if we know an Arcos URL — without
  // it we'd ship a useless half-built href. The folder/point id still
  // shows in plain text so a user can copy it manually.
  const folderHref = arcosBase && alarm.arcos_folder_id
    ? `${arcosBase}/assets/${alarm.arcos_folder_id}` : null

  const triggerStr = alarm.value_at_trigger == null ? '—'
    : `${alarm.value_at_trigger}${alarm.unit ? ' ' + alarm.unit : ''}`
  const limitStr = alarm.limit == null ? null
    : `limit ${alarm.limit}${alarm.unit ? ' ' + alarm.unit : ''}`

  return (
    <Box sx={{
      mb: 3, p: 2.5, borderRadius: 2,
      border: '1px solid #FCD34D',
      bgcolor: (t) => t.palette.mode === 'light' ? '#FFFBEB' : 'rgba(252, 211, 77, 0.06)',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SensorsOutlinedIcon sx={{ fontSize: 16, color: '#92400E' }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E' }}>
            Source telemetry
          </Typography>
        </Stack>
        {folderHref && (
          <Button
            component="a"
            href={folderHref}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: 12, fontWeight: 600, textTransform: 'none',
              color: '#92400E', borderColor: '#FCD34D',
              '&:hover': { borderColor: '#F59E0B', bgcolor: '#FEF3C7' },
            }}
          >
            Open in Arcos
          </Button>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
        <TelemetryKv label="Point" value={alarm.point_name ?? alarm.arcos_point_id ?? '—'} />
        <TelemetryKv label="Alarm type" value={alarm.type ?? '—'} />
        <TelemetryKv label="Value at trigger" value={triggerStr} mono />
        {limitStr && <TelemetryKv label="Threshold" value={limitStr} mono />}
        {alarm.raised_at && <TelemetryKv label="Raised at" value={formatDate(alarm.raised_at)} />}
        {alarm.cleared_at && <TelemetryKv label="Cleared at" value={formatDate(alarm.cleared_at)} />}
      </Box>

      {Array.isArray(alarm.tags) && alarm.tags.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {alarm.tags.map((t) => (
            <Chip key={t} label={t} size="small"
              sx={{ height: 20, fontSize: 10.5, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function TelemetryKv({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#92400E', opacity: 0.7 }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: 13, fontWeight: 600, color: 'text.primary', mt: 0.25,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
      }}>
        {value}
      </Typography>
    </Box>
  )
}

function InfoCard({ icon: Icon, label, color, children }: { icon: React.ElementType; label: string; color: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <Icon sx={{ fontSize: 14, color }} />
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
          {label}
        </Typography>
      </Stack>
      {children}
    </Box>
  )
}

function timeAgo(date: string, formatDateShort: (d: Date | string) => string) {
  const ms = Date.now() - new Date(date).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return formatDateShort(date)
}

// ─── Create request dialog ─────────────────────────────────────────────────

function CreateRequestDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [siteId, setSiteId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [locationName, setLocationName] = useState('')

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const createM = useMutation({
    mutationFn: () => api.requests.create({
      title: title.trim(),
      description: description.trim() || undefined,
      site_id: siteId || undefined,
      location_id: locationId || undefined,
    }),
    onSuccess: () => { toast.success('Request submitted!'); onCreated() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 3.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 99, bgcolor: '#D9770614', color: '#D97706',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Report an issue</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Tell us what needs fixing — our maintenance team will review it.</Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <DialogContent sx={{ px: 3.5, py: 3 }}>
        <Stack spacing={2.5}>
          <TextField label="What's the problem?" required value={title} onChange={(e) => setTitle(e.target.value)}
            size="small" fullWidth autoFocus placeholder="e.g. AC not working in Room 204"
            helperText="A short summary of the issue" />

          <TextField label="More details" value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth multiline minRows={3}
            placeholder="Describe the issue — when did it start? How urgent is it? Is it affecting work?"
            helperText="The more detail you provide, the faster we can help" />

          <Divider />

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Where is the issue?</Typography>
            <SearchableSelect
              value={siteId}
              onChange={(sid) => { setSiteId(sid); setLocationId(''); setLocationName('') }}
              options={sites}
              getValue={(s) => s.id}
              getLabel={formatSiteName}
              placeholder="Select site"
              renderOption={(s) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                  <Typography sx={{ fontSize: 13 }}>{s.name}</Typography>
                </Stack>
              )}
            />
          </Box>

          {siteId && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Specific location (optional)</Typography>
              <TreeLocationPicker siteId={siteId} value={locationId} displayName={locationName || undefined}
                onChange={(id, name) => { setLocationId(id); setLocationName(name) }}
                onClear={() => { setLocationId(''); setLocationName('') }} />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <Box sx={{ px: 3.5, py: 2.5 }}>
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button onClick={onClose} sx={{ color: 'text.secondary', fontSize: 13 }}>Cancel</Button>
          <Button variant="contained" disabled={!title.trim() || createM.isPending} onClick={() => createM.mutate()}
            sx={{ borderRadius: 2, fontWeight: 700, fontSize: 13, px: 4 }}>
            {createM.isPending ? 'Submitting...' : 'Submit request'}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  )
}

// ─── Edit request dialog ──────────────────────────────────────────────────

function EditRequestDialog({ request: r, onClose, onSaved }: { request: MaintenanceRequest; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(r.title)
  const [description, setDescription] = useState(r.description ?? '')
  const [siteId, setSiteId] = useState(r.site_id ?? '')
  const [locationId, setLocationId] = useState(r.location_id ?? '')
  const [locationName, setLocationName] = useState(r.location_name ?? '')

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 60_000 })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const updateM = useMutation({
    mutationFn: () => api.requests.update(r.id, {
      title: title.trim(),
      description: description.trim() || undefined,
      site_id: siteId || undefined,
      location_id: locationId || undefined,
    }),
    onSuccess: () => { toast.success('Request updated'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit request</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="What's the problem?" required value={title} onChange={(e) => setTitle(e.target.value)}
            size="small" fullWidth autoFocus />
          <TextField label="More details" value={description} onChange={(e) => setDescription(e.target.value)}
            size="small" fullWidth multiline minRows={3} />

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Site</Typography>
            <SearchableSelect
              value={siteId}
              onChange={(sid) => { setSiteId(sid); setLocationId(''); setLocationName('') }}
              options={sites}
              getValue={(s) => s.id}
              getLabel={formatSiteName}
              placeholder="Select site"
              renderOption={(s) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                  <Typography sx={{ fontSize: 13 }}>{s.name}</Typography>
                </Stack>
              )}
            />
          </Box>

          {siteId && (
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Specific location</Typography>
              <TreeLocationPicker siteId={siteId} value={locationId} displayName={locationName || undefined}
                onChange={(id, name) => { setLocationId(id); setLocationName(name) }}
                onClear={() => { setLocationId(''); setLocationName('') }} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!title.trim() || updateM.isPending} onClick={() => updateM.mutate()}
          sx={{ borderRadius: 2, fontWeight: 700 }}>
          {updateM.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
